import React from 'react';
import { useTranslation } from 'react-i18next';

import FullCalendar from '@fullcalendar/react';
import allLocales from '@fullcalendar/core/locales-all';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';

import { NewCalendarRangeNavigator } from './NewCalendarRangeNavigator';
import { NewCalendarToolbar } from './NewCalendarToolbar';
import { CalendarFilters } from './CalendarFilters';
import { CalendarEventContent } from './CalendarEventContent';

import {
  NEW_CALENDAR_HEADER_TOOLBAR,
  NEW_CALENDAR_EVENT_LIMITS,
  NEW_CALENDAR_MODES,
  NEW_CALENDAR_PERIODS,
  NEW_CALENDAR_SIZING,
  NEW_CALENDAR_TIME_RANGE,
  NEW_CALENDAR_VIEW_OPTIONS,
  NEW_CALENDAR_VIEWS,
} from '../newCalendar.config';
import { stackTimeGridEvents } from '../newCalendar.helpers';

const CALENDAR_PLUGINS = [dayGridPlugin, timeGridPlugin, listPlugin];
const STACKED_DAY_HEADER_VIEWS = new Set([
  NEW_CALENDAR_VIEWS[NEW_CALENDAR_MODES.CALENDAR][NEW_CALENDAR_PERIODS.DAY],
  NEW_CALENDAR_VIEWS[NEW_CALENDAR_MODES.CALENDAR][NEW_CALENDAR_PERIODS.WEEK],
]);
const VERBOSE_DAY_HEADER_VIEWS = new Set([
  NEW_CALENDAR_VIEWS[NEW_CALENDAR_MODES.LIST][NEW_CALENDAR_PERIODS.DAY],
  NEW_CALENDAR_VIEWS[NEW_CALENDAR_MODES.LIST][NEW_CALENDAR_PERIODS.WEEK],
  NEW_CALENDAR_VIEWS[NEW_CALENDAR_MODES.LIST][NEW_CALENDAR_PERIODS.MONTH],
]);

function createDayHeaderRenderer(locale) {
  const longDateFormatter = new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  const shortWeekdayFormatter = new Intl.DateTimeFormat(locale, {
    weekday: 'short',
  });

  return function renderDayHeaderContent(arg) {
    if (VERBOSE_DAY_HEADER_VIEWS.has(arg.view.type)) {
      return (
        <span className="new-calendar-day-header new-calendar-day-header--verbose">
          {longDateFormatter.format(arg.date)}
        </span>
      );
    }

    if (!STACKED_DAY_HEADER_VIEWS.has(arg.view.type)) {
      return arg.text;
    }

    const weekday = shortWeekdayFormatter.format(arg.date).toLocaleUpperCase(locale);

    return (
      <span className="new-calendar-day-header">
        <span className="new-calendar-day-header__weekday">{weekday}</span>
        <span className="new-calendar-day-header__day">{arg.date.getDate()}</span>
      </span>
    );
  };
}

function usesStackedTimeGrid(activeMode, activePeriod) {
  return activeMode === NEW_CALENDAR_MODES.CALENDAR && (
    activePeriod === NEW_CALENDAR_PERIODS.DAY || activePeriod === NEW_CALENDAR_PERIODS.WEEK
  );
}

export function NewCalendarContent({
  activeMode,
  activePeriod,
  currentRangeLabel,
  fullCalendarRef,
  onDatesSet,
  onEventClick,
  onModeChange,
  onNextPeriodClick,
  onPreviousPeriodClick,
  onPeriodChange,
  onTodayClick,
  calendarEvents,
  advancedFilters,
  onAdvancedFiltersChange,
  pmas,
  members,
  hasActiveFilters,
  isLoading,
  selectedDate,
}) {
  const { i18n, t } = useTranslation();
  const calendarLocale = i18n.resolvedLanguage || i18n.language || 'en';
  const renderDayHeaderContent = React.useMemo(() => createDayHeaderRenderer(calendarLocale), [calendarLocale]);
  const displayedEvents = React.useMemo(
    () => (
      usesStackedTimeGrid(activeMode, activePeriod)
        ? stackTimeGridEvents(calendarEvents)
        : calendarEvents
    ),
    [activeMode, activePeriod, calendarEvents],
  );
  const maxVisibleTimeSlotEvents = NEW_CALENDAR_EVENT_LIMITS[activePeriod];
  const handleEventClick = React.useCallback(
    (eventInfo) => onEventClick(eventInfo.event.extendedProps.raw),
    [onEventClick],
  );
  const renderEventContent = React.useCallback(
    (eventInfo) => (
      <CalendarEventContent
        eventInfo={eventInfo}
        maxVisibleTimeSlotEvents={maxVisibleTimeSlotEvents}
        onReminderClick={onEventClick}
      />
    ),
    [maxVisibleTimeSlotEvents, onEventClick],
  );
  const initialScrollTime = React.useMemo(() => {
    const hourBeforeNow = Math.max(7, Math.min(22, new Date().getHours() - 1));
    return `${String(hourBeforeNow).padStart(2, '0')}:00:00`;
  }, []);
  const shouldShowFilteredEmptyState =
    activeMode === NEW_CALENDAR_MODES.CALENDAR &&
    hasActiveFilters &&
    !isLoading &&
    calendarEvents.length === 0;

  return (
    <main>
      <header className="new-calendar-header">
        <NewCalendarToolbar
          activeMode={activeMode}
          activePeriod={activePeriod}
          onModeChange={onModeChange}
          onPeriodChange={onPeriodChange}
        />

        <CalendarFilters filters={advancedFilters} onChange={onAdvancedFiltersChange} pmas={pmas} members={members} />

        <NewCalendarRangeNavigator
          label={currentRangeLabel}
          onNextClick={onNextPeriodClick}
          onPreviousClick={onPreviousPeriodClick}
          onTodayClick={onTodayClick}
        />
      </header>

      <div className="new-calendar">
        <FullCalendar
          ref={fullCalendarRef}
          locale={calendarLocale}
          locales={allLocales}
          plugins={CALENDAR_PLUGINS}
          initialView={NEW_CALENDAR_VIEWS[activeMode][activePeriod]}
          initialDate={selectedDate?.toDate?.() || selectedDate}
          views={NEW_CALENDAR_VIEW_OPTIONS}
          firstDay={1}
          weekends={true}
          allDaySlot={true}
          slotMinTime={NEW_CALENDAR_TIME_RANGE.min}
          slotMaxTime={NEW_CALENDAR_TIME_RANGE.max}
          nowIndicator={true}
          scrollTime={initialScrollTime}
          scrollTimeReset={false}
          height="100%"
          expandRows={NEW_CALENDAR_SIZING.expandRows}
          handleWindowResize={NEW_CALENDAR_SIZING.handleWindowResize}
          headerToolbar={NEW_CALENDAR_HEADER_TOOLBAR}
          stickyHeaderDates={NEW_CALENDAR_SIZING.stickyHeaderDates}
          windowResizeDelay={NEW_CALENDAR_SIZING.windowResizeDelay}
          dayHeaderContent={renderDayHeaderContent}
          events={displayedEvents}
          eventClick={handleEventClick}
          noEventsContent={t('calendar-no-filter-results')}
          eventContent={renderEventContent}
          datesSet={onDatesSet}
        />
        {shouldShowFilteredEmptyState && (
          <div className="new-calendar__empty-state" role="status">
            {t('calendar-no-filter-results')}
          </div>
        )}
      </div>
    </main>
  );
}
